import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, CardContent, Checkbox, Divider, Stack, Typography } from '@mui/joy';
import { Link } from 'react-router-dom';
import type { WorkflowTask } from '../../components/ProjectWorkflow';
import type { Project } from '../../types/project';
import { documentsService } from '../../modules/docs/services/documents.service';
import type { Document } from '../../modules/docs/types/documents.types';
import { documentDisplayRef } from '../../modules/docs/utils/invoiceLedger';
import { JobReviewPanel } from '../job-records/JobReviewPanel';
import { JOB_RECORD_BLOCKS_COMPLETE_MESSAGE } from '../job-records/jobReview.model';
import { workspaceLifecycleStatus } from '../../utils/workflowUtils';

interface AftercareStagePanelProps {
  project: Project;
  tasks: WorkflowTask[];
  supplyOnly: boolean;
  onCompleteWorkspace?: () => Promise<void>;
  completing?: boolean;
  completeError?: string | null;
}

const AFTERCARE_TASKS = [
  'Request payment',
  'Receive payment',
  'Send Receipt',
  'Request Review',
] as const;

function formatMoney(value?: number | null) {
  if (value == null || Number.isNaN(Number(value))) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(Number(value));
}

const AftercareStagePanel: React.FC<AftercareStagePanelProps> = ({
  project,
  tasks,
  supplyOnly: _supplyOnly,
  onCompleteWorkspace,
  completing = false,
  completeError = null,
}) => {
  const [invoices, setInvoices] = useState<Document[]>([]);
  const [jobRecordComplete, setJobRecordComplete] = useState(false);
  const closed = workspaceLifecycleStatus(project) === 'Completed';
  const cancelled = workspaceLifecycleStatus(project) === 'Cancelled';

  useEffect(() => {
    let cancelledLoad = false;
    documentsService.getDocumentsByProject(project.id)
      .then((docs) => {
        if (!cancelledLoad) setInvoices(docs.filter((doc) => doc.kind === 'Invoice'));
      })
      .catch(() => {
        if (!cancelledLoad) setInvoices([]);
      });
    return () => {
      cancelledLoad = true;
    };
  }, [project.id]);

  return (
    <Stack gap={2}>
      <Card variant="outlined">
        <CardContent>
          <JobReviewPanel
            projectId={project.id}
            onStatusChange={(_, complete) => setJobRecordComplete(complete)}
          />
        </CardContent>
      </Card>

      {!cancelled && (
        <Card variant="outlined">
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={1.5}>
              <Stack gap={0.25}>
                <Typography level="title-md">Complete workspace</Typography>
                <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                  {closed
                    ? 'This workspace is already complete.'
                    : jobRecordComplete
                      ? 'The Job Record is complete, so this workspace can be closed.'
                      : JOB_RECORD_BLOCKS_COMPLETE_MESSAGE}
                </Typography>
              </Stack>
              <Button
                disabled={closed || !jobRecordComplete || !onCompleteWorkspace}
                loading={completing}
                onClick={() => { void onCompleteWorkspace?.(); }}
              >
                {closed ? 'Completed' : 'Complete workspace'}
              </Button>
            </Stack>
            {completeError && <Alert color="danger" sx={{ mt: 1.5 }}>{completeError}</Alert>}
          </CardContent>
        </Card>
      )}

      <Card variant="outlined">
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography level="title-md">Invoices</Typography>
            <Button size="sm" variant="outlined" component={Link} to={`/projects/${project.id}/workspace/invoices`}>
              Open invoices
            </Button>
          </Stack>
          <Divider sx={{ mb: 1.5 }} />
          {invoices.length === 0 && (
            <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
              No invoice linked. Assign one on the Invoices stage.
            </Typography>
          )}
          <Stack gap={1}>
            {invoices.map((doc) => (
              <Stack key={doc.id} direction="row" justifyContent="space-between" gap={1} flexWrap="wrap">
                <Typography level="body-sm">
                  {documentDisplayRef(doc)} · {doc.status} · {formatMoney(doc.grossTotal)}
                </Typography>
                <Button
                  size="sm"
                  variant="plain"
                  component={Link}
                  to={`/projects/${project.id}/workspace/invoices?doc=${encodeURIComponent(doc.id)}`}
                >
                  Open
                </Button>
              </Stack>
            ))}
          </Stack>
        </CardContent>
      </Card>
      <Card variant="outlined">
        <CardContent>
          <Typography level="title-md" sx={{ mb: 1 }}>Aftercare checklist</Typography>
          <Divider sx={{ mb: 1.5 }} />
          <Stack gap={1}>
            {AFTERCARE_TASKS.map((name) => {
              const task = tasks.find((item) => item.task === name);
              return (
                <Checkbox
                  key={name}
                  checked={!!task?.completed}
                  disabled
                  label={name}
                />
              );
            })}
          </Stack>
          <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 1.5 }}>
            Tick these on the lifecycle bar so the old project page stays in sync.
          </Typography>
        </CardContent>
      </Card>
      <Card variant="outlined">
        <CardContent>
          <Typography level="title-md" sx={{ mb: 1 }}>Snagging</Typography>
          <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
            Placeholder. No snagging store yet — this tab is the home for it when we add one.
          </Typography>
        </CardContent>
      </Card>
    </Stack>
  );
};

export default AftercareStagePanel;
